import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit3, PauseCircle, PlayCircle, RefreshCw } from "lucide-react";
import {
  getResearchDialogue,
  holdResearchDialogue,
  releaseResearchDialogue,
} from "../../../Services/adminResearchLibrary";
import ConfirmDialog from "../../../Components/admin/shared/ConfirmDialog";
import { Button } from "../../../ui/button";
import { Card, CardContent } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import DialogueArtworkPanel from "./DialogueArtworkPanel";
import EditDialogueDialog from "./EditDialogueDialog";
import { DialogueStatusBadge, TypeBadge } from "./badges";
import {
  EvidenceSection,
  OverridesNote,
  OverviewSection,
  ParentsSection,
  StagesSection,
  VisualsSection,
} from "./DetailSections";
import {
  displayTitle,
  errorMessage,
  holdReason,
  isAdminHeld,
} from "./researchLibraryFormat";

const AdminResearchDialogueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pending, setPending] = useState(null); // "hold" | "release"

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        setData(await getResearchDialogue(id));
      } catch (err) {
        setError(errorMessage(err, "Failed to load the dialogue"));
        if (!quiet) setData(null);
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    load();
  }, [load]);

  const dialogue = data?.dialogue;

  const handleConfirm = async (reason) => {
    setActionError(null);
    try {
      if (pending === "hold") {
        await holdResearchDialogue(dialogue.research_dialogue_id, reason);
      } else if (pending === "release") {
        await releaseResearchDialogue(dialogue.research_dialogue_id);
      }
      await load({ quiet: true });
    } catch (err) {
      setActionError(errorMessage(err, `Failed to ${pending} the dialogue`));
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !dialogue) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/research-library")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error || "Dialogue not found."}
        </div>
      </div>
    );
  }

  const held = !dialogue.runtime_attachable;
  const adminHeld = isAdminHeld(dialogue);
  const parentIds = dialogue.parent_dialogue_ids || [];
  const raw = JSON.stringify(data, null, 2);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/research-library")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight">
                {displayTitle(dialogue)}
              </h1>
              <TypeBadge type={dialogue.dialogue_type} />
              <DialogueStatusBadge dialogue={dialogue} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              <code className="text-xs">{dialogue.research_dialogue_id}</code>
              {dialogue.title_qualifier ? ` · ${dialogue.title_qualifier}` : ""}
              {dialogue.life_area ? ` · ${dialogue.life_area}` : ""}
              {dialogue.library_version ? ` · ${dialogue.library_version}` : ""}
            </p>
            {held && (
              <p className="text-sm text-amber-700 mt-1">
                Held{adminHeld ? " by an admin" : " by the release"}:{" "}
                {holdReason(dialogue) || "no reason recorded"}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => load({ quiet: true })}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Edit3 className="mr-2 h-4 w-4" /> Edit
          </Button>
          {adminHeld ? (
            <Button onClick={() => setPending("release")}>
              <PlayCircle className="mr-2 h-4 w-4" /> Release
            </Button>
          ) : (
            <Button variant="destructive" onClick={() => setPending("hold")}>
              <PauseCircle className="mr-2 h-4 w-4" /> Hold
            </Button>
          )}
        </div>
      </div>

      {actionError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      <OverridesNote overrides={dialogue.admin_overrides} />

      <DialogueArtworkPanel dialogueId={dialogue.research_dialogue_id} />

      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="overview">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="stages">
                Stages ({data.foundations?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="evidence">Evidence</TabsTrigger>
              <TabsTrigger value="parents">
                Composite parents ({parentIds.length})
              </TabsTrigger>
              <TabsTrigger value="visuals">
                Visual brief ({data.visuals?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="raw">Raw</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-4">
              <OverviewSection dialogue={dialogue} overview={data.overview} />
            </TabsContent>
            <TabsContent value="stages" className="pt-4">
              <StagesSection foundations={data.foundations} />
            </TabsContent>
            <TabsContent value="evidence" className="pt-4">
              <EvidenceSection evidence={data.evidence_payload} />
            </TabsContent>
            <TabsContent value="parents" className="pt-4">
              <ParentsSection parentIds={parentIds} />
            </TabsContent>
            <TabsContent value="visuals" className="pt-4">
              <VisualsSection visuals={data.visuals} />
            </TabsContent>
            <TabsContent value="raw" className="pt-4">
              <details>
                <summary className="cursor-pointer text-sm font-medium">
                  Show raw JSON
                </summary>
                <pre className="mt-2 max-h-[600px] overflow-auto rounded-md bg-muted/40 p-3 text-xs">
                  {raw}
                </pre>
              </details>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <EditDialogueDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        dialogue={dialogue}
        onSaved={() => load({ quiet: true })}
      />

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={
          pending === "hold"
            ? `Hold ${dialogue.research_dialogue_id}?`
            : `Release ${dialogue.research_dialogue_id}?`
        }
        description={
          pending === "hold"
            ? "A held dialogue is never attached at runtime. The hold survives every re-import until released."
            : "Returns the dialogue to the release's own rule — a composite without methodology stays held."
        }
        confirmLabel={pending === "hold" ? "Hold" : "Release"}
        destructive={pending === "hold"}
        withReason={pending === "hold"}
        reasonRequired
        reasonLabel="Reason (required)"
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default AdminResearchDialogueDetail;
